import { useEffect, useState } from "react";
import { Alert, Button, DataTable, DetailList } from "../../../ui";
import type { DataColumn } from "../../../ui";
import type { Schemas } from "../../workspace/api.generated";
import type { Command } from "../../workspace/model";
import { failure } from "../../workspace/behavior";
import { bankName, businessCodeLabel, date, money } from "../../workspace/model";
import {
  CommandDialog,
  PageHeader,
  Panel,
  StateNotice,
  handleAdminLinkNavigation,
  statusBadge,
} from "../../workspace/shared";
import { GuidedActionButton } from "../../workspace/GuidedActionButton";

type DetailState = "ready" | "loading" | "error" | "not-found";

export interface TransferRelatedAction {
  readonly label: string;
  readonly href?: string;
  readonly onClick: () => void;
}

export interface TransferDetailViewProps {
  readonly transfer?: Schemas["TransferAdminView"];
  readonly state?: DetailState;
  readonly canExecute: boolean;
  readonly unresolved: boolean;
  readonly relatedAction?: TransferRelatedAction;
  readonly memberHref?: string;
  readonly onOpenMember?: () => void;
  readonly onBack: () => void;
  readonly backHref?: string;
  readonly backLabel?: string;
  readonly detailHref?: string;
  readonly onRetry: () => void;
  readonly onRefresh: () => Promise<Schemas["TransferAdminView"] | undefined>;
  readonly onRequestRetry?: () => void;
  readonly onCommand: (command: Command) => Promise<unknown>;
}

const attemptColumns: readonly DataColumn<Schemas["ProviderAttemptView"]>[] = [
  { key: "attempt", title: "시도", numeric: true, render: (row) => row.attemptNo },
  { key: "operation", title: "작업", render: (row) => businessCodeLabel(row.operationType) },
  { key: "status", title: "상태", render: (row) => statusBadge(row.status) },
  { key: "result", title: "결과", render: (row) => businessCodeLabel(row.resultCode) },
  { key: "requested", title: "요청일시", render: (row) => date(row.requestedAt) },
  { key: "responded", title: "응답일시", render: (row) => date(row.respondedAt) },
];

function relatedReference(transfer: Schemas["TransferAdminView"]): { label: string; value: number | string } {
  if (transfer.transactionId) return { label: "연결 거래 ID", value: transfer.transactionId };
  if (transfer.refundId) return { label: "연결 환불 ID", value: transfer.refundId };
  if (transfer.benefitClaimId) return { label: "연결 혜택 ID", value: transfer.benefitClaimId };
  return { label: "연결 업무", value: "확인 필요" };
}

export function TransferDetailView({
  transfer,
  state = "ready",
  canExecute,
  unresolved,
  relatedAction,
  memberHref,
  onOpenMember,
  onBack,
  backHref,
  backLabel,
  detailHref,
  onRetry,
  onRefresh,
  onCommand,
  onRequestRetry,
}: TransferDetailViewProps) {
  const [executeOpen, setExecuteOpen] = useState(false);
  const [uncertain, setUncertain] = useState(unresolved);
  const [failedRetryVerified, setFailedRetryVerified] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    setExecuteOpen(false);
    setUncertain(unresolved);
    setFailedRetryVerified(false);
    setRefreshing(false);
    setPageError("");
  }, [transfer?.id]);

  useEffect(() => {
    setUncertain(unresolved);
  }, [unresolved]);

  useEffect(() => {
    if (transfer?.status !== "FAILED") setFailedRetryVerified(false);
  }, [transfer?.status]);

  if (!transfer || state !== "ready") {
    return (
      <>
        <PageHeader
          id="ADM-S10"
          title="송금 상세"
          description="송금 상태와 실행 이력을 확인합니다."
          breadcrumbs={[{ label: "송금 관리", href: "/transfers" }, { label: "송금 상세", href: detailHref ?? "#" }]}
          backAction={backHref ? { label: backLabel ?? "목록으로 돌아가기", href: backHref, onClick: onBack } : undefined}
          action={!backHref ? <Button size="small" variant="quiet" onClick={onBack}>송금 목록으로</Button> : undefined}
        />
        {state === "not-found" ? <StateNotice state="not-found" /> : null}
        {state === "loading" ? <p role="status">송금 정보를 불러오는 중입니다.</p> : null}
        {state === "error" ? (
          <Alert persistent variant="danger" title="송금 정보를 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={onRetry}>다시 조회</Button>}>
            잠시 후 다시 조회해 주세요.
          </Alert>
        ) : null}
      </>
    );
  }

  const transactionTransfer = transfer.purpose === "TRANSACTION_PAYOUT" && Boolean(transfer.transactionId);
  const retryReady = transfer.status === "FAILED" && transfer.lastErrorCode !== "ADMIN_CANCEL" && failedRetryVerified;
  const executableStatus = retryReady;
  const scheduledAt = Date.parse(transfer.scheduledAt);
  const transferDue = !Number.isFinite(scheduledAt) || scheduledAt <= Date.now();
  const retryAction = transfer.availableActions?.find(action => action.code === "TRANSFER_RETRY");
  const executable = transactionTransfer && canExecute && executableStatus && transferDue && !uncertain && Boolean(retryAction?.allowed);
  const executeBlockReason = transfer.lastErrorCode === "ADMIN_CANCEL" ? "관리자가 취소한 송금은 재시도할 수 없습니다." : !retryAction?.allowed ? retryAction?.blockedReasonMessage ?? "서버에서 재시도 가능 상태를 확인하지 못했습니다." : !canExecute
    ? "송금 재실행 권한이 없습니다. 권한 관리자에게 요청해 주세요."
    : uncertain ? "이전 요청 결과가 확정되지 않았습니다. 최신 상태를 조회해 주세요."
      : !failedRetryVerified ? "최신 상태 조회로 실패 상태를 다시 확인한 뒤 재실행해 주세요."
        : !transferDue ? `예약 송금일 ${date(transfer.scheduledAt)} 이후 처리할 수 있습니다.`
          : "실패한 거래 송금만 재실행할 수 있습니다.";
  const requested = uncertain || transfer.status === "PROCESSING" || transfer.status === "UNKNOWN";
  const reference = relatedReference(transfer);

  const refreshFromPage = async () => {
    if (refreshing) return undefined;
    setRefreshing(true);
    setPageError("");
    try {
      const latest = await onRefresh();
      setUncertain(false);
      setFailedRetryVerified(latest?.status === "FAILED");
      return latest;
    } catch (cause) {
      setPageError(failure(cause).message);
      throw cause;
    } finally {
      setRefreshing(false);
    }
  };

  const runCommand = async (command: Command) => {
    setUncertain(true);
    setFailedRetryVerified(false);
    try {
      return await onCommand(command);
    } catch (cause) {
      if (!failure(cause).reconcile) setUncertain(false);
      throw cause;
    }
  };

  const executeLabel = requested
    ? "결과 확인 중"
    : transfer.status === "FAILED" && !failedRetryVerified
      ? "최신 상태 확인 후 재실행"
      : "실패 송금 재실행";

  return (
    <>
      <PageHeader
        id="ADM-S10"
        title="송금 상세"
        description="송금 상태와 자동 처리 결과를 확인하고, 실패 건만 권한에 따라 복구합니다."
        breadcrumbs={[{ label: "송금 관리", href: "/transfers" }, { label: "송금 상세", href: detailHref ?? "#" }]}
        backAction={backHref ? { label: backLabel ?? "목록으로 돌아가기", href: backHref, onClick: onBack } : undefined}
        meta={(
          <p className="admin-transaction-meta" data-numeric>
            <span>{transfer.transferNo}</span>
            {statusBadge(transfer.status)}
          </p>
        )}
        action={(
          <div className="admin-inline-actions">
            <Button isLoading={refreshing} size="small" variant="quiet" onClick={() => { void refreshFromPage().catch(() => undefined); }}>최신 상태 조회</Button>
            {!backHref ? <Button size="small" variant="quiet" onClick={onBack}>송금 목록으로</Button> : null}
            {transactionTransfer && transfer.status === "FAILED" ? (
              <GuidedActionButton size="small" blockedReason={executable ? null : executeBlockReason} onClick={() => onRequestRetry ? onRequestRetry() : setExecuteOpen(true)}>{executeLabel}</GuidedActionButton>
            ) : null}
          </div>
        )}
      />
      {uncertain ? <StateNotice state="conflict" onRefresh={() => { void refreshFromPage().catch(() => undefined); }} /> : null}
      {pageError ? <Alert persistent variant="danger" title="상태 조회 실패">{pageError}</Alert> : null}
      {!canExecute && transfer.status === "FAILED" ? <Alert persistent variant="information" title="송금 재실행 권한 없음">현재 계정은 실패 상태와 이력만 조회할 수 있습니다.</Alert> : null}
      {!transactionTransfer ? (
        <Alert
          persistent
          variant="information"
          title="이 화면에서 실행할 수 없는 송금"
          action={relatedAction ? <a className="admin-row-link" href={relatedAction.href ?? "#"} onClick={(event) => handleAdminLinkNavigation(event, relatedAction.onClick)}>{relatedAction.label}</a> : undefined}
        >
          {transfer.purpose === "CUSTOMER_REFUND"
            ? "고객 환불 송금은 환불 운영에서 환불 상태와 수단을 확인한 뒤 처리해 주세요."
            : "연결된 업무와 처리 경로를 확인해 주세요. 거래 송금 실행 API를 사용하지 않습니다."}
        </Alert>
      ) : null}
      {transactionTransfer && transfer.status === "READY" && !transferDue ? (
        <Alert persistent variant="information" title="예약 송금 대기">
          {date(transfer.scheduledAt)} 이후 배치가 자동으로 처리합니다. 정상 예약 송금은 관리자 화면에서 수동으로 실행하지 않습니다.
        </Alert>
      ) : null}
      {transactionTransfer && transfer.status === "READY" && transferDue ? (
        <Alert persistent variant="information" title="자동 송금 처리 대기">
          정상 거래 송금은 회원의 선택 또는 예약 배치가 처리합니다. 관리자 화면에서는 수동으로 실행하지 않습니다.
        </Alert>
      ) : null}
      {transfer.status === "FAILED" ? (
        <Alert persistent variant="warning" title="송금 결과 확인 필요">
          실패 코드: {businessCodeLabel(transfer.lastErrorCode)}. 자동으로 다시 실행하지 않습니다. 최신 상태와 계좌 정보를 확인해 주세요.
        </Alert>
      ) : null}
      {requested ? (
        <Alert persistent variant="information" title="송금 결과 확인 중">
          최신 결과가 확인될 때까지 같은 요청을 다시 보낼 수 없습니다.
        </Alert>
      ) : null}
      <div className="admin-split">
        <Panel title="송금 정보">
          <DetailList items={[
            { label: "목적", value: transfer.purpose === "CUSTOMER_REFUND" ? "고객 환불" : businessCodeLabel(transfer.purpose) },
            { label: "회원 ID", value: memberHref ? <a className="admin-row-link" href={memberHref} onClick={onOpenMember ? (event) => handleAdminLinkNavigation(event, onOpenMember) : undefined}>{transfer.memberId}</a> : transfer.memberId },
            { ...reference, value: relatedAction ? <a className="admin-row-link" href={relatedAction.href ?? "#"} onClick={(event) => handleAdminLinkNavigation(event, relatedAction.onClick)}>{reference.value}</a> : reference.value },
            { label: "상태", value: statusBadge(transfer.status) },
            { label: "요청 금액", value: money(transfer.requestedAmount) },
            { label: "완료 금액", value: money(transfer.transferredAmount) },
            { label: "예정일시", value: date(transfer.scheduledAt) },
            { label: "완료일시", value: date(transfer.completedAt) },
          ]} />
        </Panel>
        <Panel title="수취 계좌">
          <DetailList items={[
            { label: "은행", value: `${bankName(transfer.bankCode)} (${transfer.bankCode})` },
            { label: "계좌", value: transfer.accountNumberMasked },
            { label: "예금주", value: transfer.accountHolderMasked },
          ]} />
        </Panel>
      </div>
      <Panel title="외부 처리 시도">
        {transfer.attempts.length > 0
          ? <DataTable caption="송금 시도 이력" columns={attemptColumns} rows={transfer.attempts} rowKey={(row) => row.id} />
          : <p>아직 송금 처리 시도가 없습니다.</p>}
      </Panel>
      <CommandDialog
        open={executeOpen}
        onClose={() => setExecuteOpen(false)}
        title={retryReady ? "실패한 송금을 다시 실행할까요?" : "송금을 실행할까요?"}
        description="수취 계좌와 금액을 확인했습니다. 처리 결과가 확정되기 전에는 다시 실행하지 마세요."
        operation={retryReady ? "실패 송금 재실행" : "송금 실행"}
        target={`${transfer.transferNo} · ${transfer.accountNumberMasked}`}
        amount={money(transfer.requestedAmount)}
        confirmLabel={retryReady ? "재실행 요청" : "송금 실행 요청"}
        reasonRequired={false}
        confirmDisabled={!executable}
        onCommand={runCommand}
        onRefresh={async () => { await refreshFromPage(); }}
        onComplete={async () => { await refreshFromPage(); }}
        buildCommand={() => ({
          operation: "POST /api/v1/admin/transfers/{transferId}/execute",
          params: { transferId: transfer.id },
          body: undefined,
        })}
      />
    </>
  );
}
